import {
  ActionButton,
  Button,
  Container,
  Icon,
  InputRadio,
  Notifications,
  SearchBox,
  Spinner,
  Text,
} from "@jds/core";
import classes from "./FacesPage.module.css";
import React, { useEffect, useRef, useState } from "react";
import useApiHttp from "../../hooks/use-http";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import JdsModal from "../../components/UI/JdsModal";
import ClusterStatus from "../../components/UI/ClusterStatus/ClusterStatus";
import {
  facesPageNameSearch,
  facesPageRequestIdSearch,
  incrementFacesPage,
  setFaces,
  setFacesPageDataClear,
  setFacesPageLastVisibleIndex,
  setFacesPageSort,
} from "../../store/state-slices/facesPage";
import { VirtuosoGrid } from "react-virtuoso";
import Loader from "../../components/UI/Loader/Loader";
import brokenImg from "../../assets/png/brokenImg.png";
import { LazyLoadImage } from "react-lazy-load-image-component";
import NameOfFaceForm from "../../components/UI/Form/NameOfFaceForm";
import MuiPopover from "../../components/UI/MuiPopover";
import { imgValidationCheck, validateFileSignature } from "../../util/helper";
import PageLoader from "../../components/UI/Loader/PageLoader";
import EmptyState from "../../components/NoData/EmptyState";

const gridComponents = {
  Item: ({ children, ...props }) => (
    <div {...props} className={`${classes["grid-item"]}`}>
      {children}
    </div>
  ),
  List: React.forwardRef(({ style, children }, ref) => (
    <div ref={ref} className={`${classes["grid-list"]}`} style={style}>
      {children}
    </div>
  )),
};

const FacesPage = () => {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const {
    facesList,
    page,
    lastPage,
    totalCount,
    loadedPages,
    lastVisibleIndex,
    sortKey,
    sortOrder,
    searchValue,
    requestId,
  } = useSelector((state) => state.facePage);
  const dispatch = useDispatch();
  const virtuosoRef = useRef(null);
  const searchImgInputRef = useRef(null);
  const currentAbort = useRef(null);
  const { sendRequest: getPhotoList, isLoading: isGetPhotoListLoading } =
    useApiHttp();
  const { sendRequest: searchByNameRequest, isLoading: issearchByNameLoading } =
    useApiHttp();
  const { sendRequest: searchImgRequest, isLoading: searchImgLoading } =
    useApiHttp();
  const { sendRequest: resquestIdSearch, isLoading: resquestIdSearchLoading } =
    useApiHttp();
  const { sendRequest: clusterStatusReq, isLoading: isclusterStatusLoading } =
    useApiHttp();
  const [faceData, setFaceData] = useState({});
  const [isEditModalClose, setEditModalClose] = useState(true);

  const [clusterStatus, setClusterStatus] = useState(null);
  const [refetchAftClu, setRefetchAftClu] = useState(false);
  const [clusterModalCls, setClusterModalCls] = useState(true);
  const [isPoolLoading, setIsPoolLoading] = useState(false);
  const [intervalId, setIntervalId] = useState(null);
  const [clearPoolRequestInterval, setClearPoolRequestInterval] =
    useState(false);
  const clusterInProgress =
    clusterStatus?.status !== "COMPLETED" || clusterStatus?.hasNewUploads;
  const clusteringCompleted =
    clusterStatus?.stage === "CROP_FACES" &&
    clusterStatus?.status === "COMPLETED" &&
    !clusterStatus?.hasNewUploads;

  const getFaceImages = () => {
    const abortController = new AbortController();
    currentAbort.current = abortController;
    getPhotoList(
      {
        url: `face/clusters?eventId=${eventId}&size=50&page=${page}${
          sortKey && sortOrder ? `&sort=${sortKey},${sortOrder}` : ""
        }`,
        method: "GET",
        base: "MEDIA",
        signal: abortController.signal,
      },
      null,
      (data) => {
        dispatch(setFaces({ ...data, page }));
      },
      (err, data) => {
        Notifications.toast.error({ id: "face-list", title: data?.message });
      },
    );
  };

  const searchFaceByName = () => {
    const abortController = new AbortController();
    currentAbort.current = abortController;
    searchByNameRequest(
      {
        url: `face/search/name?name=${searchValue}&eventId=${eventId}&size=50&page=${page}`,
        base: "MEDIA",
        signal: abortController.signal,
      },
      null,
      (data) => {
        dispatch(setFaces({ ...data, page }));
      },
      (err, data) => {
        Notifications.toast.error({ id: "search_face", title: data?.message });
      },
    );
  };

  const searchFaceByImage = (file) => {
    const formData = new FormData();
    formData.append("uploadFile", file);
    formData.append("eventId", eventId);
    searchImgRequest(
      {
        url: `face/search-image`,
        method: "POST",
        base: "MEDIA",
        formData: true,
        body: formData,
      },
      null,
      (data) => {
        if (data?.data?.requestId) {
          dispatch(facesPageRequestIdSearch(data?.data?.requestId));
          setIsPoolLoading(true);
        } else {
          Notifications.toast.error({
            id: "search-face",
            title: "Please try a different image.",
          });
        }
      },
      (err, data) => {
        Notifications.toast.error({ id: "search-face", title: data.message });
      },
    );
  };

  const searchByRequestId = (id) => {
    resquestIdSearch(
      {
        url: `face/pool-search-result?requestId=${id}`,
        method: "GET",
        base: "MEDIA",
      },
      null,
      (data) => {
        if (data?.data) {
          setClearPoolRequestInterval(true);
          setIsPoolLoading(false);
          dispatch(setFaces({ ...data, page }));
        }
      },
    );
  };

  useEffect(() => {
    clusterStatusReq(
      {
        url: `face/cluster/status/${eventId}`,
        method: "GET",
        base: "MEDIA",
      },
      null,
      (data) => {
        setClusterStatus(data?.data);
      },
      (err) => {
        setClusterStatus(null);
      },
    );
  }, []);

  useEffect(() => {
    if (clearPoolRequestInterval && intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
      setClearPoolRequestInterval(false);
    }
  }, [clearPoolRequestInterval, intervalId]);

  useEffect(() => {
    // ✅ Only fetch if this page was NOT already loaded
    if (!loadedPages.includes(page) && !requestId) {
      if (!searchValue) {
        getFaceImages();
      } else {
        searchFaceByName();
      }
    }
    return () => {
      currentAbort?.current?.abort();
    };
  }, [page, sortKey, sortOrder, requestId, searchValue]);

  useEffect(() => {
    if (requestId) {
      const id = setInterval(() => {
        searchByRequestId(requestId);
      }, 5000);
      setIntervalId(id);

      // Clear the interval on component unmount
      return () => clearInterval(id);
    }
  }, [requestId]);

  return (
    <div>
      {/* Face Page Header */}
      <div className={`${classes["faces-page-header"]}`}>
        <div className={`${classes["faces-page-heading"]}`}>
          <Text text={`Faces `} appearance="heading-xs" />
          <Text
            text={`${totalCount ? ` - ${totalCount}` : ""}`}
            appearance="body-m"
            color="primary-grey-80"
          />
        </div>
        <div className={`${classes["header-options-container"]}`}>
          {clusterStatus?.status && facesList.length > 0 && (
            <div
              className={`btn--blinking btn--blinking-${
                clusterInProgress
                  ? "negative"
                  : clusteringCompleted
                    ? "positive"
                    : ""
              }`}>
              <ActionButton
                kind="secondary"
                label="Faces Status"
                icon="IcScanFace"
                size="medium"
                appearance={
                  clusterInProgress
                    ? "negative"
                    : clusteringCompleted
                      ? "positive"
                      : ""
                }
                onClick={() => {
                  setClusterModalCls(false);
                }}
              />
            </div>
          )}
          <MuiPopover
            trigger={{
              component: SortBtn,
            }}>
            <SortComponent
              sortKeyPar={sortKey}
              sortOrderPar={sortOrder}
              virtuosoRef={virtuosoRef}
            />
          </MuiPopover>
          <div>
            <input
              ref={searchImgInputRef}
              type="file"
              accept=".png, .jpg, .jpeg"
              onChange={(e) => {
                const file = e.target.files[0];
                if (imgValidationCheck(e)) {
                  validateFileSignature(file, {
                    jpeg: ["ffd8ffe0", "ffd8ffe1", "ffd8ffe2", "ffd8ffdb"], // JPEG signatures can vary
                    png: ["89504e47"],
                  })
                    .then((isValid) => {
                      if (isValid) {
                        searchFaceByImage(e.target.files[0]);
                      } else {
                        Notifications.toast.error({
                          id: "search-face-img",
                          title:
                            "Please upload a valid image file (JPG, JPEG and PNG).",
                        });
                      }
                    })
                    .catch((error) => {
                      Notifications.toast.error({
                        id: "search-face-img",
                        title:
                          "Please upload a valid image file (JPG, JPEG and PNG).",
                      });
                    });
                } else {
                  Notifications.toast.error({
                    id: "search-face-img",
                    title:
                      "Please upload a valid image file (JPG, JPEG and PNG).",
                  });
                }
              }}
            />
            <SearchBox
              label="Search Faces"
              prefix="IcSearch"
              value={searchValue}
              suffix={
                !requestId && searchValue.length === 0 ? "IcPhoto" : "IcError"
              }
              onSuffixClick={() => {
                if (requestId || searchValue.length !== 0) {
                  dispatch(facesPageRequestIdSearch(null));
                } else {
                  if (searchValue.length === 0) {
                    if (searchImgInputRef.current) {
                      searchImgInputRef.current.value = null; //reset file
                      searchImgInputRef.current.click();
                    }
                  }
                }
              }}
              onChange={(e) => {
                const searchValue = e.target.value;
                dispatch(facesPageNameSearch(searchValue));
              }}
            />
          </div>
        </div>
      </div>

      {/* Face Container */}
      {facesList.length > 0 && (
        <div
          style={{
            height: "calc(100vh - 180px)",
          }}>
          <div className={`${classes["grid-container"]}`}>
            <VirtuosoGrid
              ref={virtuosoRef}
              className="custom-scrollbar"
              data={facesList}
              totalCount={totalCount}
              endReached={() => {
                if (page < lastPage) {
                  dispatch(incrementFacesPage());
                }
              }}
              overscan={200}
              components={{
                ...gridComponents,
                Footer: isGetPhotoListLoading ? Footer : null,
              }}
              initialTopMostItemIndex={lastVisibleIndex || 0}
              rangeChanged={(range) => {
                dispatch(setFacesPageLastVisibleIndex(range.startIndex));
              }}
              itemContent={(index, face) => (
                <FaceDiv
                  face={face}
                  index={index}
                  setFaceData={(val) => {
                    setFaceData(val);
                  }}
                  setEditModalClose={(val) => {
                    setEditModalClose(val);
                  }}
                />
              )}
            />
          </div>
        </div>
      )}

      {/* Cluster status Modal */}
      <JdsModal
        size="m"
        // header="Your Photo Is Being Prepared."
        kind="custom"
        close={clusterModalCls}
        onClose={() => {
          setClusterModalCls(true);
        }}
        headerProps={{ divider: "false" }}>
        {!clusterModalCls && (
          <ClusterStatus
            clusterStatusProp={clusterStatus}
            setClusterStatusProp={setClusterStatus}
            setRefetchAftClu={setRefetchAftClu}
            clusteringAttempt={clusterStatus?.clusteringAttempt || 0}
          />
        )}
      </JdsModal>

      {/* NameOfFace form Modal */}
      <JdsModal
        header="Name of the Face"
        size="m"
        close={isEditModalClose}
        onClose={() => {
          setEditModalClose(true);
        }}>
        {!isEditModalClose && (
          <NameOfFaceForm
            data={faceData}
            // isSuccess={(bool, name, id) => {
            //   setFaceName(name);
            //   setFacesList((pre) => {
            //     const updateList = pre.map((item) => {
            //       if (item.clusterId == id) {
            //         return { ...item, username: name };
            //       } else {
            //         return item;
            //       }
            //     });
            //     return updateList;
            //   });
            // }}
          />
        )}
      </JdsModal>

      {/* Page Loader */}
      {(searchImgLoading || resquestIdSearchLoading || isPoolLoading) && (
        <PageLoader
          open={
            issearchByNameLoading ||
            searchImgLoading ||
            resquestIdSearchLoading ||
            isPoolLoading ||
            isGetPhotoListLoading
          }
          label={isPoolLoading && "Please wait for a while..."}
          labelprops={{ color: "white" }}
        />
      )}

      {(issearchByNameLoading || isGetPhotoListLoading) && page == 0 && (
        <Container
          fullWidth
          horizontalAlign="center"
          verticalAlign="center"
          style={{ minHeight: "220px" }}>
          <Spinner kind="primary" label="Loading..." size="large" />
        </Container>
      )}

      {/* Empty State */}

      {facesList?.length == 0 &&
        (searchValue.length > 0 || requestId) &&
        !issearchByNameLoading &&
        !isPoolLoading && (
          <div className={`${classes["no-data-container"]}`}>
            {" "}
            <EmptyState
              title="No faces found!"
              subtitle="Try removing or adjusting your filters to see more results."
              action={false}
            />
          </div>
        )}

      {facesList?.length == 0 &&
        searchValue.length == 0 &&
        !requestId &&
        !isGetPhotoListLoading && (
          <div className={`${classes["no-data-container"]}`}>
            <EmptyState title="No faces found!" subtitle="" action={false} />

            {clusterStatus?.status && clusterStatus?.status !== "COMPLETED" ? (
              <>
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    fontFamily: "var(--font-medium)",
                  }}>
                  <Icon ic={"IcScanFace"} />
                  <Text
                    appearance="body-m"
                    text={"We're processing your photos!"}
                    as="span"
                    color="primary_grey_80"
                  />
                </div>
                <Button
                  label="Show details"
                  onClick={() => {
                    console.log("clicked");
                    setClusterModalCls(false);
                  }}
                />
              </>
            ) : (
              <Text
                appearance="body-m"
                text={"Start uploading to see the detected faces."}
                as="span"
                color="primary_grey_80"
              />
            )}
          </div>
        )}
    </div>
  );
};

export default FacesPage;

/* ===============================
   FOOTER LOADER
   =============================== */
const Footer = () => (
  <Loader size="small" containerProps={{ style: { marginTop: 16 } }} />
);

const FaceDiv = ({ face, index, setFaceData, setEditModalClose }) => {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const [errorImg, setErrorImg] = useState(false);
  return (
    <div key={index}>
      <div className={`${classes["image-card"]}`}>
        {/* Image */}
        <img
          src={errorImg ? brokenImg : `${face?.mediaUrl}&size=s`}
          alt=""
          loading="lazy"
          onError={() => {
            setErrorImg(true);
          }}
          onClick={() => {
            navigate(
              `/home/events/faces/${eventId}/face-list/${face?.clusterId}`,
              {
                state: {
                  username: face?.username,
                  mediaUrl: face?.mediaUrl,
                },
              },
            );
          }}
          draggable={false}
        />
      </div>
      <div>
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (face?.username) {
              setEditModalClose(false);
              setFaceData(face);
            }
          }}
          style={{
            cursor: face?.username ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            padding: "12px",
            color: face?.username ? "var(--color-primary-50)" : "transparent",
            justifyContent: "center",
          }}>
          <p
            style={{
              fontFamily: "var(--font-medium)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              margin: "0 !important",
              fontSize: "18px",
            }}
            title={face?.username}>
            {face?.username || "Unnamed"}
          </p>

          <span style={{ display: "flex" }}>{face?.username && <IcPen />}</span>
        </div>
      </div>
    </div>
  );
};

const IcPen = () => (
  <svg
    width="20px"
    height="20px"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.5 4.5a3.54 3.54 0 00-5 0l-.29.29 5 5 .29-.29a3.54 3.54 0 000-5zm-13.95 9a3 3 0 00-.76 1.3l-1 3.65a1.5 1.5 0 001.81 1.81l3.65-1.05a3 3 0 001.3-.76l7.24-7.24-5-5-7.24 7.29z"
      fill="currentColor"></path>
  </svg>
);

const SortBtn = (props) => (
  <Button
    iconLeft={"IcSort"}
    label={props?.label || "Sort"}
    kind="tertiary"
    {...props}
  />
);

const SortComponent = ({
  close = () => {},
  sortKeyPar = null,
  sortOrderPar = null,
  virtuosoRef = null,
}) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  useEffect(() => {
    setSortKey(sortKeyPar);
    setSortOrder(sortOrderPar);
  }, [sortKeyPar, sortOrderPar]);

  return (
    <div
      style={{
        padding: "10px",
        width: "215px",
      }}>
      <div className={"sort-block"}>
        <Text text="Sort by" appearance="" />
        <div className={"sort-ele"}>
          <InputRadio
            size="small"
            value="username"
            onClick={(e) => {
              setSortKey(e.target.value);
            }}
            checked={sortKey == "username" ? true : false}
          />
          <span
            onClick={(e) => {
              setSortKey("username");
            }}>
            <Text text="Name" color="primary_grey_80" />
          </span>
        </div>{" "}
        <div className={"sort-ele"}>
          <InputRadio
            size="small"
            value="imageCount"
            onClick={(e) => {
              setSortKey(e.target.value);
            }}
            checked={sortKey == "imageCount" ? true : false}
          />
          <span
            onClick={(e) => {
              setSortKey("imageCount");
            }}>
            <Text text="Count" color="primary_grey_80" />
          </span>
        </div>
        <Text text="Sort order" appearance="" />
        <div className={"sort-ele"}>
          <InputRadio
            size="small"
            value="asc"
            onClick={(e) => {
              setSortOrder(e.target.value);
            }}
            checked={sortOrder == "asc" ? true : false}
          />
          <span
            onClick={(e) => {
              setSortOrder("asc");
            }}>
            <Text
              text={sortKey == "username" ? "A to Z" : "Low on top"}
              color="primary_grey_80"
            />
          </span>
        </div>{" "}
        <div className={"sort-ele"}>
          <InputRadio
            size="small"
            value="desc"
            onClick={(e) => {
              setSortOrder(e.target.value);
            }}
            checked={sortOrder == "desc" ? true : false}
          />
          <span
            onClick={(e) => {
              setSortOrder("desc");
            }}>
            <Text
              text={sortKey == "username" ? "Z to A" : "High on top"}
              color="primary_grey_80"
            />
          </span>
        </div>{" "}
      </div>
      <div style={{ marginTop: "16px" }}>
        <Button
          fullWidth
          kind="secondary"
          size="small"
          label="Apply Sort"
          onClick={() => {
            dispatch(setFacesPageSort({ sortKey, sortOrder }));
            virtuosoRef?.current?.scrollToIndex({
              index: 0,
              align: "start",
              behavior: "auto",
            });
            close();
          }}
          disabled={!sortKey || !sortOrder}
        />
      </div>
      <div style={{ marginTop: "16px" }}>
        <Button
          fullWidth
          size="small"
          label="Clear Sort"
          onClick={() => {
            setSortOrder(null);
            setSortKey(null);
            dispatch(setFacesPageSort({ sortKey: null, sortOrder: null }));
            virtuosoRef?.current?.scrollToIndex({
              index: 0,
              align: "start",
              behavior: "auto",
            });
            close();
          }}
          disabled={!(sortKeyPar && sortOrderPar)}
        />
      </div>
    </div>
  );
};
